'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import Image from 'next/image';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import RichText from '../block-ui/RichText';
import BlockWrapper from '../BlockWrapper';
import EditablePropsType from '../../../types/composer';
import headingSchema from '../../../lib/composer/schemas/heading';
import subheadingSchema from '../../../lib/composer/schemas/subheading';
import themeSchema, { getTextColor } from '../../../lib/composer/schemas/theme';
import textSchema from '../../../lib/composer/schemas/text';
import { themeToTailwindClassMap } from '../../../lib/composer/schemas/theme';
import { textColorClassMap, textGray } from '@osmosis/tailwind-ui/styles/text';
import { FormattedMessage, useIntl } from 'react-intl';

type Testimonial = {
  text: string;
  image: string;
  author: string;
};

type TestimonialCarouselProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  testimonials: Testimonial[];
  theme?: string;
  interval?: number;
  topHeader?: boolean;
};

type TestimonialCarouselComponent = React.FC<TestimonialCarouselProps> & {
  schema?: any;
};

/**
 * TestimonialCarousel displays a carousel of testimonials with optional heading, subheading, and text.
 * @param {TestimonialCarouselProps} props - The props for the carousel component.
 * @returns {JSX.Element} The rendered testimonial carousel.
 */
const TestimonialCarousel: TestimonialCarouselComponent = (props) => {
  const safeInterval = Math.max(Number(props.interval) || 1, 1);

  const {
    heading,
    subheading,
    text,
    testimonials,
    theme = 'primaryLightest',
    interval = safeInterval, // use normalized value
    topHeader = false,
  } = props;

  const intl = useIntl();
  const [index, setIndex] = useState(0);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const touchStartX = useRef<number | null>(null);
  // Normalize interval to at least 1 for both display and logic
  const normalizedInterval = Math.max(Number(interval) || 1, 1);

  const intervalSec = normalizedInterval;

  useEffect(() => {
    timer.current = setTimeout(() => {
      setIndex((i) => (i + 1) % testimonials.length);
    }, intervalSec * 1000); // convert seconds to ms
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [index, testimonials.length, intervalSec]);

  const handleSelect = (i: number) => {
    setIndex(i);
    timer.current && clearTimeout(timer.current);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      setIndex((prev) =>
        delta > 0
          ? (prev - 1 + testimonials.length) % testimonials.length
          : (prev + 1) % testimonials.length,
      );
    }
    touchStartX.current = null;
  };

  const current =
    testimonials && testimonials.length > 0 ? testimonials[index] : undefined;

  const mouseStartX = useRef<number | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseStartX.current = e.clientX;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (mouseStartX.current === null) return;
    const delta = e.clientX - mouseStartX.current;
    if (Math.abs(delta) > 50) {
      setIndex((prev) =>
        delta > 0
          ? (prev - 1 + testimonials.length) % testimonials.length
          : (prev + 1) % testimonials.length,
      );
    }
    mouseStartX.current = null;
  };

  const textColor = getTextColor(theme);

  const bgTheme = themeToTailwindClassMap[theme];

  const textColorClass = textColorClassMap[textColor] || textGray;

  return (
    <Box
      className={`${bgTheme} ${textColorClass}`}
      data-testid="testimonials-carousel"
    >
      <BlockWrapper>
        <Flex className={`flex flex-col items-center text-center mb-4`}>
          {heading && (
            <Heading text={heading} theme={theme} topHeader={topHeader} />
          )}
          {subheading && (
            <Subheading text={subheading} theme={theme} topHeader={topHeader} />
          )}
          {text && <RichText html={text} theme={theme} />}
        </Flex>
        {current ? (
          <Box
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            // select-none is used to prevent text selection while dragging
            className={`bg-${theme} max-w-[600px] mx-auto rounded-lg p-4 select-none ${textColorClass}`}
          >
            <P className="mb-4 text-lg italic text-center">{current.text}</P>

            {/* // object fit (show whole image) */}
            <Image
              src={current.image}
              alt={current.author}
              width={160}
              height={160}
              className="w-full max-w-[160px] h-auto block mx-auto mt-[35px] mb-[25px] rounded-lg object-contain"
            />

            <P className="font-bold text-center">{current.author}</P>
          </Box>
        ) : (
          <Box className="text-center text-gray-500">
            <FormattedMessage
              id="testimonials.none"
              defaultMessage="No testimonials available."
            />
          </Box>
        )}

        <Flex
          className="justify-center mt-4 gap-2"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          {testimonials.map((_, i) => {
            const ariaLabel = intl.formatMessage(
              {
                id: 'testimonial.carousel.aria',
                defaultMessage: 'Go to testimonial {number}',
              },
              { number: i + 1 },
            );
            return (
              <button
                key={i}
                onClick={() => handleSelect(i)}
                data-testid={`testimonial-dot-${i}`}
                className={`w-3 h-3 rounded-full border-none cursor-pointer rounded-full ${
                  i === index ? 'bg-blue-500' : 'bg-gray-300'
                }`}
                aria-label={ariaLabel}
              >
                <span className="sr-only">{ariaLabel}</span>
              </button>
            );
          })}
        </Flex>
      </BlockWrapper>
    </Box>
  );
};

TestimonialCarousel.schema = {
  name: 'testimonials-carousel',
  label: 'Testimonials Carousel',
  editableProps: [
    headingSchema,
    subheadingSchema,
    textSchema,
    {
      label: 'Testimonials',
      name: 'testimonials',
      type: EditablePropsType.Array,
      items: {
        type: EditablePropsType.Object,
        name: 'testimonial',
        label: 'Testimonial',
        properties: {
          text: {
            label: 'Testimonial Text',
            name: 'text',
            type: EditablePropsType.Text,
          },
          image: {
            label: 'Image URL',
            name: 'image',
            type: EditablePropsType.Text,
          },
          author: {
            label: 'Author',
            name: 'author',
            type: EditablePropsType.Text,
          },
        },
      },
    },
    themeSchema,
    {
      label: 'Auto-Rotate Interval (seconds)',
      name: 'interval',
      type: EditablePropsType.Text,
      default: 5,
    },
  ],
};

export default TestimonialCarousel;
